// Added by Sejune Oh on 2019-03-14
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ElementTimeStampProps } from '@/helper/define'
import { getQueryPath } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Root store
import Rootstore from '../../index'

// Store
import {
    reqGetDicomScp,
    reqGetDicomScps,
    reqCreateDicomScp,
    reqDeleteDicomScp,
    reqUpdateDicomScp,
    reqGetAssignedDataStoragesToDicomScp,
    reqAssignDataStoragesToDicomScp,
    reqRestartAllDicomScpInTheGroup,
} from './DicomScpStore.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface DicomScpElementProps extends ElementTimeStampProps {
    id?: string
    fk_node_id?: string
    fk_dicom_scp_ruleset_id?: string
    token?: string
    fk_group_id?: string
    name?: string
    description?: string
    store_scp_enabled?: boolean
    echo_scp_enabled?: boolean
    qr_scp_enabled?: boolean
    ae_title?: string
    ip_address?: string
    port?: string
    peer_checked?: boolean
    anonymized?: boolean
}

export interface DisplableDicomScpElementProps extends DicomScpElementProps {
    advanced?: boolean
    use_advanced_rule?: boolean
    default_storage_id?: string
    is_converted?: boolean
    linked_storage_ids?: Array<number>
    group_key?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class DicomScpStore {
    _totalCount: number = 0
    _elements: Array<DicomScpElementProps | DisplableDicomScpElementProps> = []
    _cachedDicomScp?: DisplableDicomScpElementProps
    _selectedGroupId?: string

    root?: Rootstore

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    constructor(root: Rootstore) {
        this.root = root
        makeAutoObservable(this)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    getElement = async (id: string) => {
        const { data: rtnElement } = await reqGetDicomScp(id)
        return rtnElement
    }

    getElements = async (path: string) => {
        const {
            data: { total_count, items },
        } = await reqGetDicomScps(path)
        this._setTotalCount(parseInt(total_count!))
        return items!
    }

    addElement = async (element: DisplableDicomScpElementProps) => {
        delete element.advanced
        delete element.default_storage_id
        delete element.linked_storage_ids
        delete element.use_advanced_rule

        const { data: rtnElement } = await reqCreateDicomScp(element)
        return rtnElement
    }

    removeElement = async (id: string) => {
        return await reqDeleteDicomScp(id)
    }

    updateElement = async (id: string, element: DisplableDicomScpElementProps) => {
        const { data: updatedElement } = await reqUpdateDicomScp(id, element)
        return updatedElement.id
    }

    getAssignedDataStorages = async (id: string) => {
        const {
            data: { items },
        } = await reqGetAssignedDataStoragesToDicomScp(id)
        return items
    }

    updateAssignedDataStorages = async (id: string, dataStoragdIds: Array<string>) => {
        return await reqAssignDataStoragesToDicomScp(id, dataStoragdIds)
    }

    checkDuplicatedName = async (name: string) => {
        const path = getQueryPath({ pageIndex: 0, orderBy: 'name', filter: JSON.stringify({ $op: 'and', name })})
        
        const elements = await this.getElements(path)
        return elements.length > 0
    }

    checkDuplicatedAETitle = async (ae_title: string) => {
        const filter = { $op: 'and', ae_title: ae_title, fk_group_id: this._selectedGroupId }
        const path = getQueryPath({ pageIndex: 0, orderBy: 'ae_title', filter: JSON.stringify(filter) })

        const elements = await this.getElements(path)
        return elements.length > 0
    }

    checkDuplicatedAddr = async ({ ip_address, port }: DisplableDicomScpElementProps) => {
        const getQuery = getQueryPath({
            filter: JSON.stringify({ $op: 'and', ip_address, port }),
        })
        const reqList = [this.getElements(getQuery)]
        
        const [rtn, rtnDup] = await Promise.all([...reqList])
        if (rtn && rtn.length !== 0) return true
        if (rtnDup && rtnDup.length !== 0) return true
        return false
    }

    restartDICOMScpsInSameGroup = async (fk_group_id: string) => {
        return await reqRestartAllDicomScpInTheGroup(fk_group_id)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    _setTotalCount = (count: number) => {
        this._totalCount = count
    }

    setElements = (elements: Array<DicomScpElementProps | DisplableDicomScpElementProps>) => {
        this._elements = elements
    }

    setDicomScpListWithGroupInfo = (
        gwElements: Array<DicomScpElementProps | DisplableDicomScpElementProps>,
        groupNameMap: Map<string, string>
    ) => {
        this._elements = gwElements.map((element) => {
            return {
                ...element,
                group_key: groupNameMap.get(element.fk_group_id!),
            }
        })
    }

    cacheStore = (dicomScp: DicomScpElementProps) => {
        this._cachedDicomScp = dicomScp
        this._selectedGroupId = this._cachedDicomScp.fk_group_id
    }

    clearCachedStore = () => {
        this._cachedDicomScp = undefined
        this._selectedGroupId = undefined
    }

    setGroupId(fkGroupId: string) {
        this._selectedGroupId = fkGroupId
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get elements() {
        return this._elements
    }

    get cachedDicomScp() {
        return this._cachedDicomScp
    }

    get totalCount() {
        return this._totalCount
    }
}
